/**
 * Resume a checkout from a Paddle recovery email (PLAN-LAUNCH B3).
 *
 * Paddle's abandoned-checkout emails link back to the app with
 * `?_paction=recovery&_ptxn=<transaction id>`. The mechanism they are built on
 * is Paddle.js scanning the URL as it initialises — which never happens here,
 * because `lib/paddle.ts` deliberately keeps the script off the boot path and
 * loads it only when a purchase is attempted. Without this component the
 * recovery link is a dead one: the app boots, the params sit in the address
 * bar, and the person who came back to pay is looking at the recipe list.
 *
 * So this component, mounted once above the router, is the missing scan: see
 * the `_ptxn` param, load Paddle, reopen the overlay for that transaction.
 *
 * ## Why it waits for `/api/config`
 *
 * Opening the overlay needs the deployment's client token, which arrives on
 * `/api/config` after first render. Until it has, the param stays untouched in
 * the URL — stripping it early would lose the transaction on the one boot that
 * needs it. It likewise stays when opening *fails* (an ad blocker, an offline
 * tab), so a plain reload is the retry; it is stripped only once the overlay
 * has actually answered, so neither a payment nor a deliberate close replays
 * on refresh.
 *
 * ## Why it works signed out
 *
 * A recovery email can be opened on a device that has never seen the account.
 * That is fine for the *payment*: the transaction already carries
 * `custom_data.userId` from the checkout that created it, and that id — not
 * this device's session — is what the webhook grants the plan to. What a
 * signed-out device cannot do is watch the plan arrive, because `/api/me` has
 * nobody to report on. So a signed-out completion gets a toast pointing at
 * sign-in instead of a poll that could never succeed.
 *
 * ## Toasts, not the upgrade dialog
 *
 * The plans dialog is a comparison of tiers. A person arriving from a recovery
 * email chose theirs already — it is in the transaction — so there is nothing
 * to compare and no dialog to raise. The overlay opens over the plain app, and
 * the aftermath is said in a toast.
 */

import { useEffect, useRef } from 'react';
import { waitForPlanChange } from '@/auth/client';
import { useSession } from '@/auth/session-context';
import { useToast } from '@/components/toast';
import type { CheckoutOutcome } from '@/lib/paddle';
import { clearRecoveryParams, openRecoveryCheckout, recoveryTransactionId } from '@/lib/paddle';

export function PaddleRecovery() {
  const session = useSession();
  const { toast } = useToast();
  /** One resume per page load, however often config or session re-renders us. */
  const started = useRef(false);

  const billing = session.config.billing;
  const clientToken = billing.enabled ? billing.clientToken : null;
  const signedIn = session.user !== null;
  const plan = session.entitlement.plan;
  const { refresh } = session;

  useEffect(() => {
    if (started.current || clientToken === null) return;
    const transactionId = recoveryTransactionId();
    if (transactionId === null) return;
    started.current = true;

    void (async () => {
      let outcome: CheckoutOutcome;
      try {
        outcome = await openRecoveryCheckout({ token: clientToken, transactionId });
      } catch {
        toast({
          title: 'We could not reopen your checkout.',
          description: 'Check your connection and reload the page to try again.',
          variant: 'error',
        });
        return;
      }
      clearRecoveryParams();
      // Closed by hand is somebody deciding not to buy, again. Still silent —
      // the same judgement the upgrade dialog makes.
      if (outcome !== 'completed') return;
      if (!signedIn) {
        toast({
          title: 'Your payment went through.',
          description: 'Sign in and your plan will be waiting for you.',
        });
        return;
      }
      const granted = await waitForPlanChange(plan);
      await refresh();
      toast(
        granted
          ? { title: 'Your plan is active.' }
          : {
              title: 'Your payment went through.',
              description:
                'We are still activating your plan. It will be there in a moment, or after a reload.',
            },
      );
    })();
  }, [clientToken, signedIn, plan, refresh, toast]);

  return null;
}
